import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { branding } from "@/config/branding";
import { api } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

const STEPS = [
  { key: "payment", label: "Payment Received" },
  { key: "setup", label: "Professional Setup" },
  { key: "qa", label: "Testing & QA" },
  { key: "access", label: "Account Access Granted" },
  { key: "live", label: "Go Live" },
];

export default function PurchaseConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState<{ paid: boolean; prospect?: { business_name?: string } | null } | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!sessionId) { setChecking(false); return; }
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await api.publicCheckoutStatus(sessionId);
        setStatus(s);
        if (s.paid) { setChecking(false); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= 12) { setChecking(false); return; }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const paid = !!status?.paid;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-3xl items-center px-4 sm:px-6">
          <Link to="/" className="flex items-center" data-testid="confirm-logo">
            <BrandLogo className="h-8" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-14 sm:px-6" data-testid="purchase-confirmation">
        {checking && !paid ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
            <p className="mt-4 text-sm text-slate-500">Confirming your payment with Stripe…</p>
          </div>
        ) : paid ? (
          <>
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900" data-testid="confirm-headline">Welcome to {branding.productName}. Your setup is now in progress.</h1>
              <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500">
                Your payment has been received{status?.prospect?.business_name ? ` for ${status.prospect.business_name}` : ""}. Our team will now configure your ResFil AI receptionist, business knowledge, scheduling and integrations before your account access is activated.
              </p>
            </div>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="confirm-progress">
              <ol className="space-y-4">
                {STEPS.map((s, i) => (
                  <li key={s.key} className="flex items-center gap-3" data-testid={`confirm-step-${s.key}`}>
                    {i === 0 ? (
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />
                    ) : (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-300 text-[11px] font-semibold text-slate-400">{i + 1}</span>
                    )}
                    <span className={i === 0 ? "text-sm font-medium text-slate-900" : "text-sm text-slate-500"}>{s.label}</span>
                    {i === 0 && <span className="ml-auto text-xs font-medium text-emerald-600">Done</span>}
                    {i === 1 && <span className="ml-auto text-xs font-medium text-blue-600">In progress</span>}
                  </li>
                ))}
              </ol>
            </div>

            <p className="mt-6 text-center text-sm text-slate-500">
              We'll email <b>account activation instructions</b> once your setup is complete and tested. No action is needed from you right now.
            </p>
            <div className="mt-6 flex justify-center">
              <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700" data-testid="confirm-home">Back to homepage <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <h1 className="font-display text-2xl font-semibold text-slate-900">We couldn't confirm your payment yet</h1>
            <p className="mx-auto mt-3 max-w-md text-sm text-slate-500">If you completed checkout, your confirmation email will arrive shortly. Otherwise you can pick your plan again.</p>
            <Link to="/pricing" className="mt-6 inline-block"><button className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">View plans</button></Link>
          </div>
        )}
      </main>
    </div>
  );
}
